import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  App,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Segmented,
  Select,
  Spin,
  Switch,
} from 'antd';
import { styled } from 'styled-components';
import {
  useSaveVessel,
  useSaveVesselAgent,
  useSaveVesselBerth,
  useSaveVesselJourney,
  useSaveVesselPort,
  useVesselAgents,
  useVesselBerths,
  useVesselPorts,
  useVessels,
} from '@/api/Vessel/hooks/useVesselCatalogue';
import { useAppCustomers } from '@/hooks';
import { useTranslate } from '@/translations';
import {
  SaveVesselJourneyRequest,
  SaveVesselLookupRequest,
  SaveVesselPortRequest,
  SaveVesselRequest,
  VesselLookup,
} from '@/types';
import {
  CustomTable,
  faAdd,
  faEdit,
  Icon,
  NoAccessAlert,
  TooltipIcon,
} from '@/ui/components';

const Board = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  align-items: start;
`;

const useNarrowScreen = () => {
  const query = '(max-width: 991px)';
  const [narrow, setNarrow] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const media = window.matchMedia(query);
    const onChange = () => setNarrow(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  return narrow;
};

const tableHeight = 'calc(100vh - 380px)';

const emptyPort = (): SaveVesselPortRequest => ({
  portName: '',
  locode: '',
  isActive: true,
});

const emptyVessel = (): SaveVesselRequest => ({
  vesselName: '',
  isActive: true,
});

type JourneyFormValues = {
  vesselId?: number;
  originPortId?: number;
  destinationPortId?: number;
  voyage?: string;
  eta?: dayjs.Dayjs;
};

export const ReferenceDataManagement = () => {
  const { t } = useTranslate();
  const { hasSuperAdminAccess } = useAppCustomers();
  const narrow = useNarrowScreen();
  const [pane, setPane] = useState<'vessels' | 'ports' | 'agents' | 'berths'>(
    'vessels',
  );

  if (!hasSuperAdminAccess) return <NoAccessAlert />;

  const panels = {
    vessels: <VesselsPanel />,
    ports: <PortsPanel />,
    agents: <AgentsPanel />,
    berths: <BerthsPanel />,
  };

  return narrow ? (
    <>
      <Segmented
        block
        style={{ marginBottom: 12 }}
        value={pane}
        onChange={(value) =>
          setPane(value as 'vessels' | 'ports' | 'agents' | 'berths')
        }
        options={[
          { label: t('vessels'), value: 'vessels' },
          { label: t('vesselPorts'), value: 'ports' },
          { label: t('vesselAgents'), value: 'agents' },
          { label: t('vesselBerths'), value: 'berths' },
        ]}
      />
      {panels[pane]}
    </>
  ) : (
    <Board>
      {panels.vessels}
      {panels.ports}
      {panels.agents}
      {panels.berths}
    </Board>
  );
};

const LookupPanel = ({
  title,
  rows,
  loading,
  saving,
  onSave,
}: {
  title: string;
  rows: VesselLookup[];
  loading: boolean;
  saving: boolean;
  onSave: (values: SaveVesselLookupRequest) => Promise<boolean>;
}) => {
  const { t } = useTranslate();
  const [draft, setDraft] = useState<SaveVesselLookupRequest>();
  const [form] = Form.useForm<SaveVesselLookupRequest>();

  useEffect(() => {
    if (!draft) return;
    form.setFields([
      { name: 'name', value: draft.name ?? '' },
      { name: 'isActive', value: draft.isActive },
    ]);
  }, [draft, form]);

  return (
    <Card
      title={title}
      extra={
        <Button
          shape="circle"
          onClick={() => setDraft({ name: '', isActive: true })}
        >
          <Icon icon={faAdd} />
        </Button>
      }
    >
      <CustomTable
        rowKey="id"
        loading={loading}
        dataSource={rows}
        pagination={false}
        scroll={{ y: tableHeight }}
        columns={[
          { title: t('name'), dataIndex: 'name' },
          {
            title: t('active'),
            dataIndex: 'isActive',
            width: 90,
            align: 'center',
            render: (value) => <Switch checked={value} disabled />,
          },
          {
            dataIndex: 'id',
            title: t('actions'),
            width: 90,
            align: 'center',
            render: (_, row) => (
              <TooltipIcon
                title={t('edit')}
                icon={faEdit}
                onClick={() =>
                  setDraft({
                    id: row.id,
                    name: row.name,
                    isActive: row.isActive,
                  })
                }
              />
            ),
          },
        ]}
      />
      <Modal
        title={draft?.id ? t('edit') : t('add')}
        open={Boolean(draft)}
        confirmLoading={saving}
        destroyOnHidden
        onCancel={() => setDraft(undefined)}
        onOk={() => form.submit()}
      >
        {draft ? (
          <Form
            form={form}
            layout="vertical"
            preserve={false}
            initialValues={draft}
            onFinish={async (values) => {
              const saved = await onSave({
                id: draft.id,
                name: values.name.trim(),
                isActive: values.isActive,
              });
              if (saved) setDraft(undefined);
            }}
          >
            <Form.Item
              name="name"
              label={t('name')}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="isActive"
              label={t('active')}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Form>
        ) : null}
      </Modal>
    </Card>
  );
};

const AgentsPanel = () => {
  const { t } = useTranslate();
  const { message } = App.useApp();
  const agents = useVesselAgents();
  const saveAgent = useSaveVesselAgent();

  return (
    <LookupPanel
      title={t('vesselAgents')}
      rows={agents.data ?? []}
      loading={agents.isPending || agents.isFetching}
      saving={saveAgent.isPending}
      onSave={async (values) => {
        const result = await saveAgent.mutateAsync(values);
        if (!result.success) {
          message.error(result.message || t('generalError'));
          return false;
        }
        return true;
      }}
    />
  );
};

const BerthsPanel = () => {
  const { t } = useTranslate();
  const { message } = App.useApp();
  const berths = useVesselBerths();
  const saveBerth = useSaveVesselBerth();

  return (
    <LookupPanel
      title={t('vesselBerths')}
      rows={berths.data ?? []}
      loading={berths.isPending || berths.isFetching}
      saving={saveBerth.isPending}
      onSave={async (values) => {
        const result = await saveBerth.mutateAsync(values);
        if (!result.success) {
          message.error(result.message || t('generalError'));
          return false;
        }
        return true;
      }}
    />
  );
};

const PortsPanel = () => {
  const { t } = useTranslate();
  const { message } = App.useApp();
  const ports = useVesselPorts();
  const savePort = useSaveVesselPort();
  const [draft, setDraft] = useState<SaveVesselPortRequest>();
  const [form] = Form.useForm<SaveVesselPortRequest>();

  useEffect(() => {
    if (!draft) return;
    form.setFields([
      { name: 'portName', value: draft.portName ?? '' },
      { name: 'locode', value: draft.locode ?? '' },
      { name: 'isActive', value: draft.isActive },
    ]);
  }, [draft, form]);

  const save = async (values: SaveVesselPortRequest) => {
    const result = await savePort.mutateAsync({
      id: draft?.id,
      portName: values.portName.trim(),
      locode: values.locode?.trim() || undefined,
      isActive: values.isActive,
    });
    if (!result.success) {
      message.error(t('generalError'));
      return;
    }
    setDraft(undefined);
  };

  return (
    <Card
      title={t('vesselPorts')}
      extra={
        <Button shape="circle" onClick={() => setDraft(emptyPort())}>
          <Icon icon={faAdd} />
        </Button>
      }
    >
      <CustomTable
        rowKey="id"
        loading={ports.isPending || ports.isFetching}
        dataSource={ports.data ?? []}
        pagination={false}
        scroll={{ y: tableHeight }}
        columns={[
          { title: t('portName'), dataIndex: 'portName' },
          { title: t('locode'), dataIndex: 'locode', width: 120 },
          {
            title: t('active'),
            dataIndex: 'isActive',
            width: 90,
            align: 'center',
            render: (value) => <Switch checked={value} disabled />,
          },
          {
            dataIndex: 'id',
            title: t('actions'),
            width: 90,
            align: 'center',
            render: (_, port) => (
              <TooltipIcon
                title={t('edit')}
                icon={faEdit}
                onClick={() =>
                  setDraft({
                    id: port.id,
                    portName: port.portName,
                    locode: port.locode,
                    isActive: port.isActive,
                  })
                }
              />
            ),
          },
        ]}
      />
      <Modal
        title={draft?.id ? t('edit') : t('add')}
        open={Boolean(draft)}
        confirmLoading={savePort.isPending}
        destroyOnHidden
        onCancel={() => setDraft(undefined)}
        onOk={() => form.submit()}
      >
        {draft ? (
          <Form
            form={form}
            layout="vertical"
            preserve={false}
            initialValues={draft}
            onFinish={(values) => void save(values)}
          >
            <Form.Item
              name="portName"
              label={t('portName')}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="locode" label={t('locode')}>
              <Input />
            </Form.Item>
            <Form.Item
              name="isActive"
              label={t('active')}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Form>
        ) : null}
      </Modal>
    </Card>
  );
};

const VesselsPanel = () => {
  const { t } = useTranslate();
  const { message } = App.useApp();
  const vessels = useVessels();
  const ports = useVesselPorts();
  const saveVessel = useSaveVessel();
  const saveJourney = useSaveVesselJourney();
  const [vesselDraft, setVesselDraft] = useState<SaveVesselRequest>();
  const [draft, setDraft] = useState<SaveVesselJourneyRequest>();
  const [vesselForm] = Form.useForm<SaveVesselRequest>();
  const [journeyForm] = Form.useForm<JourneyFormValues>();
  const portOptions = (ports.data ?? []).map((port) => ({
    value: port.id,
    label: port.portName,
  }));
  const saveVesselDraft = async (values: SaveVesselRequest) => {
    const result = await saveVessel.mutateAsync({
      id: vesselDraft?.id,
      vesselName: values.vesselName.trim(),
      imo: values.imo || undefined,
      isActive: values.isActive,
    });
    if (!result.success) {
      message.error(result.message || t('generalError'));
      return;
    }
    setVesselDraft(undefined);
  };

  const save = async (values: JourneyFormValues) => {
    if (!values.vesselId) return;
    const result = await saveJourney.mutateAsync({
      id: draft?.id,
      vesselId: values.vesselId,
      originPortId: values.originPortId,
      destinationPortId: values.destinationPortId,
      voyage: values.voyage?.trim() || undefined,
      plannedArrival: values.eta ? values.eta.toISOString() : undefined,
    });
    if (!result.success) {
      message.error(t('generalError'));
      return;
    }
    setDraft(undefined);
  };

  return (
    <Card
      title={t('vessels')}
      extra={
        <Button shape="circle" onClick={() => setVesselDraft(emptyVessel())}>
          <Icon icon={faAdd} />
        </Button>
      }
    >
      <CustomTable
        rowKey="id"
        loading={vessels.isPending || vessels.isFetching}
        dataSource={vessels.data ?? []}
        pagination={false}
        scroll={{ x: 480, y: tableHeight }}
        columns={[
          { title: t('imo'), dataIndex: 'imo', width: 120 },
          { title: t('vesselName'), dataIndex: 'vesselName' },
          {
            title: t('active'),
            dataIndex: 'isActive',
            width: 90,
            align: 'center',
            render: (value) => <Switch checked={value} disabled />,
          },
          {
            dataIndex: 'id',
            title: t('actions'),
            width: 90,
            align: 'center',
            render: (_, vessel) => (
              <TooltipIcon
                title={t('edit')}
                icon={faEdit}
                onClick={() =>
                  setVesselDraft({
                    id: vessel.id,
                    imo: vessel.imo,
                    vesselName: vessel.vesselName,
                    isActive: vessel.isActive,
                  })
                }
              />
            ),
          },
        ]}
      />
      <Modal
        title={`${vesselDraft?.id ? t('edit') : t('add')} ${t('vessel')}`}
        open={Boolean(vesselDraft)}
        confirmLoading={saveVessel.isPending}
        destroyOnHidden
        onCancel={() => setVesselDraft(undefined)}
        onOk={() => vesselForm.submit()}
      >
        {vesselDraft ? (
          <Form
            form={vesselForm}
            layout="vertical"
            initialValues={vesselDraft}
            onFinish={(values) => void saveVesselDraft(values)}
          >
            <Form.Item name="imo" label={t('imo')} rules={[{ required: true }]}>
              <InputNumber min={1} precision={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="vesselName"
              label={t('vesselName')}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="isActive"
              label={t('active')}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Form>
        ) : null}
      </Modal>
      <Modal
        title={`${draft?.id ? t('edit') : t('add')} Journey`}
        open={Boolean(draft)}
        confirmLoading={saveJourney.isPending}
        destroyOnHidden
        onCancel={() => setDraft(undefined)}
        onOk={() => journeyForm.submit()}
      >
        {draft ? (
          <Form
            form={journeyForm}
            layout="vertical"
            initialValues={{
              vesselId: draft.vesselId || undefined,
              originPortId: draft.originPortId,
              destinationPortId: draft.destinationPortId,
              voyage: draft.voyage,
              eta: draft.plannedArrival
                ? dayjs(draft.plannedArrival)
                : undefined,
            }}
            onFinish={(values) => void save(values)}
          >
            <Form.Item name="vesselId" label={t('vessel')} hidden>
              <Input />
            </Form.Item>
            <Form.Item
              name="voyage"
              label={t('voyage')}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="originPortId" label={t('originPort')}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                loading={ports.isPending || ports.isFetching}
                notFoundContent={
                  ports.isPending || ports.isFetching ? (
                    <Spin size="small" />
                  ) : undefined
                }
                options={portOptions}
              />
            </Form.Item>
            <Form.Item name="destinationPortId" label={t('destinationPort')}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                loading={ports.isPending || ports.isFetching}
                notFoundContent={
                  ports.isPending || ports.isFetching ? (
                    <Spin size="small" />
                  ) : undefined
                }
                options={portOptions}
              />
            </Form.Item>
            <Form.Item name="eta" label={t('etaUtc')}>
              <DatePicker
                showTime
                style={{ width: '100%' }}
                format="DD/MM/YYYY HH:mm"
              />
            </Form.Item>
          </Form>
        ) : null}
      </Modal>
    </Card>
  );
};
